import { type FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ArrowTopIcon from '@assets/img/icons/arrow-top.svg?react';
import HummerIcon from '@assets/img/icons/hammer.svg?react';
import { AppRoute } from '@common/app/routes';
import { useCheckRequirements } from '@common/hooks/check-requirements';
import { useGoodsProduction } from '@common/hooks/goods-production';
import { calcPercentage } from '@common/lib/calcPercentage';
import { getImage } from '@common/lib/images';
import { getText } from '@common/lib/text';
import {
  type ShortProductData,
  type WorkshopRequirements,
} from '@common/types/workshopType';
import { GoodsList } from '@components/Goods/GoodsList/GoodsList';
import { H1, StringSmall } from '@components/Typography/Typography';
import { MainButton } from '@components/ui/Button/Button';
import { Picture } from '@components/ui/Picture/Picture';
import { WorkshopProductionPreview } from '@components/Workshop/WorkshopProductionPreview/WorkshopProductionPreview';
import {
  selectTargetGoodsByWorkshopId,
  selectWorkshopByNameOrId,
} from '@redux/selectors/workshopSelectors';
import { useAppSelector } from '@redux/store';
import cn from 'classnames';

import { HOLIDAY_KEY } from '@/common/config/media-config';
import { factoryLevels } from '@/common/constants/levels';
import { type FactoryTypeText } from '@/common/types/factory/index';

import { LockFactory } from './LockFactory';

import css from './styles.module.scss';

interface CardFactoriesLi {
  id?: string;
  name: FactoryTypeText;
  products: ShortProductData[];
  level?: number;
  isNextToBuild?: boolean;
  isRequirenmentsComplete?: boolean;
  completePlants?: boolean;
  nextLevelRequirements?: WorkshopRequirements;
}

const CardFactoriesLi: FC<CardFactoriesLi> = memo(
  ({
    id,
    name,
    products,
    isNextToBuild,
    isRequirenmentsComplete,
    nextLevelRequirements,
    level,
  }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const activeGoods = useAppSelector((state) =>
      selectTargetGoodsByWorkshopId(state, id),
    );
    const workshop = useAppSelector((state) =>
      selectWorkshopByNameOrId(state, id),
    );
    const { producted } = useGoodsProduction({
      incommingStorage: workshop?.incommingStorage,
      localStock: workshop?.localStock,
      rate: activeGoods?.productionRateOriginal,
      plantId: workshop?.id,
    });
    // useEffect(() => {
    //   if (workshop && activeGoods)
    //     dispatch(
    //       addProductedForGood({
    //         id,
    //         producted: workshop.localStock + producted,
    //       }),
    //     );
    // }, [producted, workshop?.localStock, activeGoods]);

    const getImageSuffix = (): string => {
      if (HOLIDAY_KEY === 'helloween') return '-helloween';

      if (HOLIDAY_KEY === 'new_year' && name !== 'machinery')
        return '-new_year';

      return '';
    };

    const background = getImage({ name, field: 'background' });
    const defaultCharacterImgPath = `/img/characters/workshop/${name}`;
    const characterImgPath = `${defaultCharacterImgPath}${getImageSuffix()}`;
    const title = getText({ name, kind: 'build', t });
    const nextLevel = useCheckRequirements(nextLevelRequirements);
    const image = getImage({ name, field: 'icon' });

    const minLevel = factoryLevels[name];

    return (
      <li
        key={name}
        className={cn(
          'cardFactories__element',
          isNextToBuild && !isRequirenmentsComplete && 'grayscale',
        )}
        onClick={() => {
          if (isNextToBuild) {
            navigate(AppRoute.WORKSHOP + '/build');
          } else if (!isNextToBuild) {
            navigate(`${AppRoute.WORKSHOP}/${id}`);
          }
        }}
        // style={{
        //   backgroundImage: `image-set(url("${background?.img}") type("image/jpeg"), url("${background?.webp}") type("image/webp"))`,
        //   backgroundSize: 'cover',
        //   backgroundPositionY: '60%',
        // }}
      >
        <Picture
          img={background?.img}
          webp={background?.webp}
          className={css.background}
          alt="background"
        />
        <Picture
          className="aspect-[160/184] w-[10rem] absolute right-0 bottom-0"
          img={`${characterImgPath}.png`}
          webp={`${characterImgPath}.webp`}
          fallbackImg={`${defaultCharacterImgPath}.png`}
          fallbackWebp={`${defaultCharacterImgPath}.webp`}
          alt="character"
        />

        <div className="flex h-full items-end gap-4 w-full">
          {isNextToBuild && !isRequirenmentsComplete ? (
            <LockFactory image={image} minLevel={minLevel} />
          ) : (
            <>
              <GoodsList
                data={products}
                workshopName={name}
                isNextToBuild={isNextToBuild}
                production={
                  activeGoods && workshop?.incommingStorage !== 0
                    ? {
                        goods: activeGoods.name,
                        percentage: calcPercentage(
                          (workshop?.localStock || 0) + producted,
                          (workshop?.localStock || 0) +
                            (workshop?.incommingStorage || 0),
                        ),
                      }
                    : undefined
                }
              />
              <div className="flex flex-1">
                {!isNextToBuild && (
                  <WorkshopProductionPreview
                    producted={producted}
                    activeGoods={activeGoods}
                    localStock={workshop?.localStock}
                    incommingStorage={workshop?.incommingStorage}
                  />
                )}
                {isNextToBuild && isRequirenmentsComplete && (
                  <MainButton
                    disabled={!isRequirenmentsComplete}
                    inactive={!isRequirenmentsComplete}
                    text={t('Build')}
                    className={cn(
                      'flex items-center gap-2 max-w-[10.5rem] relative !h-9 z-10',
                      !isRequirenmentsComplete && '!bg-transparent',
                    )}
                  >
                    <HummerIcon className="text-white/40 w-4 h-4" />
                  </MainButton>
                )}
              </div>
            </>
          )}
        </div>

        <div
          className={cn(
            'absolute top-2 z-[2]  flex gap-2',
            isNextToBuild ? 'flex-col left-4' : 'flex-row items-center left-2',
          )}
        >
          {isNextToBuild ? (
            <StringSmall className="text-white/40">
              {t('Next Building')}
            </StringSmall>
          ) : (
            <div className="cardFactories__level flex items-center">
              <span>{level}</span>
              {nextLevelRequirements && nextLevel.isComplete && (
                <ArrowTopIcon className="w-4 h-4 -mr-[4px] shrink-0 text-accent" />
              )}
            </div>
          )}
          <H1 className="white-text-shadow-gradient !font-extrabold">
            {title}
          </H1>
        </div>
      </li>
    );
  },
);

export default CardFactoriesLi;
